import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Card,
    CardContent,
    Typography,
    Grid,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Box,
    Stack
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";

export function ProductsHistory({ open, onClose, historicos = [] }) {

    // Função para formatar os dados do produto de forma legível
    function formatProductData(obj) {
        const allowedFields = [
            'nome',
            'descricao',
            'indicacao',
            'precaucao',
            'observacao',
            'vida_util',
        ];

        if (!obj || typeof obj !== "object") return {};

        const formatted = {};

        Object.entries(obj).forEach(([key, value]) => {
            if (allowedFields.includes(key) && value !== null && value !== undefined) {
                formatted[translateField(key)] = value;
            }
        });

        return formatted;
    }

    function translateField(field) {
        const translations = {
            'nome': 'Nome',
            'descricao': 'Descrição',
            'indicacao': 'Indicação',
            'observacao': 'Observação',
            'vida_util': 'Vida Útil'
        };
        return translations[field] || field;
    }

    // Componente para exibir dados formatados
    function FormattedData({ data }) {
        const cleanData = formatProductData(data);

        if (!cleanData || Object.keys(cleanData).length === 0) {
            return (
                <Typography color="text.secondary" fontSize={14}>
                    Nenhum dado adicional disponível
                </Typography>
            );
        }

        return (
            <Box sx={{ mt: 1 }}>
                {Object.entries(cleanData).map(([key, value]) => (
                    <Box
                        key={key}
                        sx={{
                            mb: 2,
                            pb: 2,
                            borderBottom: '1px solid #f0f0f0',
                            '&:last-child': {
                                borderBottom: 'none',
                                mb: 0,
                                pb: 0
                            }
                        }}
                    >
                        <Typography
                            fontWeight={600}
                            fontSize={13}
                            color="primary"
                            sx={{ mb: 0.5 }}
                        >
                            {key}
                        </Typography>
                        <Typography
                            fontSize={14}
                            sx={{
                                wordBreak: 'break-word',
                                color: '#555'
                            }}
                        >
                            {typeof value === 'object'
                                ? JSON.stringify(value, null, 2)
                                : String(value)
                            }
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    }

    function formatDate(date) {
        if (!date) return "N/A";
        return dayjs(date).format("DD/MM/YYYY");
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: '90vh',
                    bgcolor: '#fafafa'
                }
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: 22,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                }}
            >
                <HistoryIcon sx={{ fontSize: 28 }} />
                Histórico de Versões do Produto
            </DialogTitle>

            <DialogContent sx={{ p: 3, bgcolor: '#fafafa', marginTop: 4 }}>
                {historicos.length === 0 ? (
                    <Card sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                        <Typography fontSize={18} color="text.secondary">
                            Nenhuma versão registrada para este produto.
                        </Typography>
                    </Card>
                ) : (
                    <Stack spacing={3}>
                        {[...historicos].reverse().map((h, index) => (
                            <Card
                                key={h.id}
                                elevation={2}
                                sx={{
                                    borderRadius: 3,
                                    border: '2px solid',
                                    borderColor: index === 0 ? 'primary.main' : '#e0e0e0',
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        boxShadow: 6,
                                        borderColor: 'primary.main'
                                    }
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    {/* Cabeçalho da versão */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 2,
                                            flexWrap: 'wrap',
                                            gap: 2
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                            <Box
                                                sx={{
                                                    bgcolor: index === 0 ? 'primary.main' : '#9e9e9e',
                                                    color: 'white',
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 700
                                                }}
                                            >
                                                {historicos.length - index}
                                            </Box>
                                            <Box>
                                                <Typography variant="h6" fontWeight={700}>
                                                    Versão #{historicos.length - index}
                                                </Typography>
                                                {index === 0 && (
                                                    <Chip
                                                        label="Última Versão"
                                                        size="small"
                                                        color="primary"
                                                        sx={{ mt: 0.5 }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ mb: 3 }} />

                                    {/* Informações principais */}
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={4}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    bgcolor: 'white',
                                                    borderRadius: 2,
                                                    border: '1px solid #e0e0e0',
                                                    height: '120px'
                                                }}
                                            >
                                                <Typography
                                                    fontWeight={600}
                                                    fontSize={12}
                                                    color="text.secondary"
                                                    textTransform="uppercase"
                                                    mb={1}
                                                >
                                                    Produto
                                                </Typography>
                                                <Typography fontWeight={600} fontSize={16} mb={1}>
                                                    {h.produto?.nome || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    bgcolor: 'white',
                                                    borderRadius: 2,
                                                    border: '1px solid #e0e0e0',
                                                    height: '120px'
                                                }}
                                            >
                                                <Typography
                                                    fontWeight={600}
                                                    fontSize={12}
                                                    color="text.secondary"
                                                    textTransform="uppercase"
                                                    mb={1}
                                                >
                                                    Certificado de Aprovação
                                                </Typography>
                                                <Typography fontWeight={600} fontSize={16} mb={1}>
                                                    {h.ca || 'N/A'}
                                                </Typography>
                                                <Typography fontSize={13} color="text.secondary">
                                                    Validade: {formatDate(h.validadeCa) || "Sem validade"}
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    bgcolor: 'white',
                                                    borderRadius: 2,
                                                    border: '1px solid #e0e0e0',
                                                    height: '120px'
                                                }}
                                            >
                                                <Typography
                                                    fontWeight={600}
                                                    fontSize={12}
                                                    color="text.secondary"
                                                    textTransform="uppercase"
                                                    mb={1}
                                                >
                                                    <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                                    Data da Mudança
                                                </Typography>
                                                <Typography fontWeight={600} fontSize={16}>
                                                    {formatDate(h.dataMudanca) || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/* Detalhes da versão */}
                                    <Accordion
                                        sx={{
                                            mt: 3,
                                            borderRadius: 2,
                                            '&:before': { display: 'none' },
                                            border: '1px solid #e0e0e0'
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{
                                                bgcolor: '#f5f5f5',
                                                borderRadius: 2,
                                                '&:hover': {
                                                    bgcolor: '#eeeeee'
                                                }
                                            }}
                                        >
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <InfoOutlinedIcon color="primary" fontSize="small" />
                                                <Typography fontWeight={600}>
                                                    Ver detalhes desta versão
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 3, bgcolor: 'white' }}>
                                            <FormattedData data={h.estadoProduto} />
                                        </AccordionDetails>
                                    </Accordion>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2.5, bgcolor: '#fafafa' }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    size="large"
                >
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
}